import React from 'react';
import cn from 'classnames';
import { Visibility } from '../../../types/common-types';
import Select from '../select';
import './style.scss';

interface ExtraPropsDate {
  y: number;
  m: number;
  d: number;
}
export interface CalendarExtraProps {
  min: ExtraPropsDate;
  max: ExtraPropsDate;
  reverseOrder?: boolean 
}
export interface CalendarValue {
  day: string;
  month: string;
  year: string;
}
interface CalendarProps {
  id?: string;
  value?: CalendarValue;
  label?: string;
  error?: boolean;
  helperText?: string;
  visibility?: Visibility;
  extra?: CalendarExtraProps;
  className?: string;
  styleName?: string;
  onChange?: (value: { id: string; value: object; error: boolean }) => void;
}

const addZero = (num: number) => {
  return num < 10 ? `0${num}` : num;
};

const monthList = () => {
  return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => ({
    value: addZero(month).toString(),
    title: month.toString(),
  }));
};

const getMaxDays = (year: string, month: string) => {
  return new Date(+year, +month, 0).getDate();
};

const daysList = (year: string, month: string) => {
  const maxDays = getMaxDays(year, month);
  const daysArr = [];
  let i = 1;
  for (i; i <= maxDays; i += 1) {
    daysArr.push({
      title: i.toString(),
      value: addZero(i).toString(),
    });
  }
  return daysArr;
};

const getLimitDate = (typeLimitVal: ExtraPropsDate) => {
  const curDate = new Date();
  const cY = curDate.getFullYear();
  const cM = curDate.getMonth() + 1;
  const cD = curDate.getDate();
  const y = cY + typeLimitVal.y;
  const m = cM + typeLimitVal.m;
  const d = cD + typeLimitVal.d;
  const newDate = new Date(y, m - 1, d);
  return newDate.getTime();
};

const yearList = (extra: CalendarExtraProps) => {
  const { min, max, reverseOrder = false } = extra;
  const minLimit = new Date(getLimitDate(min));
  const maxLimit = new Date(getLimitDate(max));
  const range = [];
  for (let i = maxLimit.getFullYear(); i >= minLimit.getFullYear(); i -= 1) {
    range.push({
      title: i.toString(),
      value: i.toString(),
    });
  }
  if (reverseOrder) {range.reverse()}
  return range;
};

const Calendar: React.FC<CalendarProps> = ({
  id = '',
  value = { day: '', month: '', year: '' },
  label,
  error = false,
  helperText,
  visibility = Visibility.Visible,
  className,
  onChange,
  extra = { min: { y: -100, m: 0, d: 0 }, max: { y: 0, m: 0, d: 0 } },
}) => {
  const maxDays = getMaxDays(value.year, value.month);
  const checkDay =
    error && (value.day.length === 0 || Number(value.day) > maxDays);
  const checkMonth = error && value.month.length === 0;
  const checkYear = error || (value.year.length < 4 && value.year.length > 1);

  const minMaxDateError = (date: CalendarValue) => {
    const minLimit = getLimitDate(extra.min);
    const maxLimit = getLimitDate(extra.max);
    const y = Number(date.year);
    const m = Number(date.month) - 1;
    const d = Number(date.day);
    const valueTime = new Date(y, m, d).getTime();
    return !(valueTime >= minLimit && valueTime <= maxLimit);
  };
  const checkError = (date: CalendarValue) => {
    return (
      date.month.length === 0 ||
      date.day.length === 0 ||
      +date.day > getMaxDays(date.year, date.month) ||
      date.year.length < 4 ||
      minMaxDateError(date)
    );
  };
  const onChangeHandler = (key: string, val: string) => {
    const date = { ...value, [key]: val };
    const newError = checkError(date);
    if (onChange) onChange({ id, value: date, error: newError });
  };
  return (
    <div
      id={id}
      className={className}
      data-cy="calendar"
      styleName={cn('calendar', {
        error,
        hidden: visibility === Visibility.Hidden,
      })}
    >
      <div styleName="calendar__label">{label}</div>
      <div styleName="calendar__items">
        <Select
          id="day"
          value={value.day}
          visibility={visibility}
          error={checkDay}
          onChange={({ value: val }) => {
            onChangeHandler('day', val);
          }}
          options={daysList(value.year, value.month)}
        />
        <Select
          id="month"
          value={value.month}
          visibility={visibility}
          error={checkMonth}
          onChange={({ value: val }) => {
            onChangeHandler('month', val);
          }}
          options={monthList()}
        />
        <Select
          id="year"
          value={value.year}
          visibility={visibility}
          error={checkYear}
          onChange={({ value: val }) => {
            onChangeHandler('year', val);
          }}
          options={yearList(extra)}
        />
      </div>
      <div data-cy-prop="errorMsg" styleName="calendar__error">
        {helperText}
      </div>
    </div>
  );
};

export default Calendar;
